import type { CollaborationEditorStatus, CollaborationTransportState } from "@workith/shared";
import { cn } from "@workith/ui";
import { Loader2, Wifi, WifiOff } from "lucide-react";
import { collaborationAnnouncement, collaborationTransportText } from "./collaborationEditorState";

interface ConnectionStatusProps {
  transport: CollaborationTransportState;
}

const TRANSPORT_TONE: Readonly<Record<CollaborationTransportState, string>> = {
  // green은 상태색 예외(킷에 success 토큰 없음), 오류는 destructive 토큰 사용
  online: "text-green-600 dark:text-green-400",
  connecting: "text-muted-foreground",
  reconnecting: "text-amber-600 dark:text-amber-400",
  offline: "text-destructive",
};

/**
 * The transport axis, as text.
 *
 * Transport is reported and nothing else: after the first sync a reconnect
 * leaves the document editable, so this must never be wired to editability. The
 * label is always rendered, because colour and icon alone are not a status for
 * anyone reading with a screen reader or without colour vision.
 */
export default function ConnectionStatus({ transport }: ConnectionStatusProps) {
  const label = collaborationTransportText(transport);
  const isRetrying = transport === "connecting" || transport === "reconnecting";
  const Icon = transport === "online" ? Wifi : isRetrying ? Loader2 : WifiOff;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 text-sm font-medium leading-none",
        TRANSPORT_TONE[transport]
      )}
    >
      <Icon className={cn("h-4 w-4 shrink-0", isRetrying && "animate-spin")} aria-hidden="true" />
      <span className="leading-none">{label}</span>
    </div>
  );
}

interface CollaborationAnnouncerProps {
  status: CollaborationEditorStatus;
  /** Terminal authorization (and recovery availability), announced once. */
  terminalAlert: string | null;
}

/**
 * The spoken half of the status.
 *
 * Transport and local-change transitions go through one polite region whose
 * text is a pure function of the state, so an unchanged state re-rendered N
 * times produces no repeated announcement. A terminal authorization answer is
 * an alert instead: it is the one transition that changes what the user may do.
 */
export function CollaborationAnnouncer({ status, terminalAlert }: CollaborationAnnouncerProps) {
  return (
    <>
      <output aria-live="polite" className="sr-only">
        {collaborationAnnouncement(status)}
      </output>
      {terminalAlert && (
        <p role="alert" className="text-xs text-destructive">
          {terminalAlert}
        </p>
      )}
    </>
  );
}
